import { useCallback, useEffect, useState } from 'react';
import { T } from '@design/index';
import { patientApi, type PharmacySubstitution } from '../api';

/**
 * Substitutes a pharmacy has proposed on this patient's orders.
 *
 * When a pharmacist cannot dispense what was prescribed they propose an
 * equivalent and wait for the patient to agree. The web app had no screen for
 * it at all, so the proposal sat unanswered at the counter while the order
 * stopped moving.
 *
 * A decision is written onto the proposal and posted into the order's chat as
 * the patient's own message, so the pharmacist sees it where they are already
 * looking. Only a proposal still awaiting an answer is actionable here.
 */

function fmtWhen(v: string): string {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 60) return `${Math.max(mins, 1)} min ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)} hr ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export default function SubstitutionRequests({ onDecided }: { onDecided?: () => void }) {
  const [rows, setRows] = useState<PharmacySubstitution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRows(await patientApi.pharmacyModule.substitutions() ?? []);
    } catch (e: any) {
      setError(e?.body?.message || 'Proposed substitutes could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const decide = async (row: PharmacySubstitution, approve: boolean) => {
    setBusyId(row.id);
    setError('');
    try {
      await patientApi.pharmacyModule.decideSubstitution(row.id, approve ? 'APPROVE' : 'DECLINE');
      setRows((rs) => rs.map((r) => (
        r.id === row.id ? { ...r, status: approve ? 'APPROVED' : 'DECLINED', decidedAt: new Date().toISOString() } : r
      )));
      onDecided?.();
    } catch (e: any) {
      // Still awaiting the patient — do not show it as answered.
      setError(e?.body?.message || 'That answer could not be sent. The pharmacy is still waiting.');
    } finally {
      setBusyId('');
    }
  };

  const waiting = rows.filter((r) => r.status === 'PENDING_APPROVAL');

  // Nothing proposed is the ordinary state; an empty card would be noise.
  if (loading || (!error && waiting.length === 0)) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      {error && (
        <div style={{
          background: T.white, border: `1px solid ${T.danger}55`, borderRadius: 14,
          padding: 12, marginBottom: 10, fontFamily: T.font, fontSize: 12, color: T.danger,
        }}>
          {error}
        </div>
      )}

      {waiting.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontFamily: T.font }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: T.text }}>A pharmacy is waiting on you</span>
            <span style={{
              fontSize: 10, fontWeight: 700, color: T.white, background: T.warning,
              borderRadius: 10, padding: '2px 8px',
            }}>
              {waiting.length}
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {waiting.map((r) => (
              <div
                key={r.id}
                style={{
                  background: T.white, border: `1px solid ${T.warning}55`, borderRadius: 16,
                  padding: 16, fontFamily: T.font, boxShadow: T.shadow,
                }}
              >
                <div style={{ fontSize: 11, color: T.textSec }}>
                  {[r.pharmacyName, r.orderNumber].filter(Boolean).join(' · ')} — {fmtWhen(r.at)}
                </div>

                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: 12.5, fontWeight: 700, color: T.textSec, textDecoration: 'line-through',
                  }}>
                    {r.medicineName || 'Prescribed medicine'}
                  </span>
                  <span style={{ fontSize: 14, color: T.textTer }}>→</span>
                  <span style={{
                    fontSize: 13, fontWeight: 800, color: T.teal, background: `${T.teal}12`,
                    border: `1px solid ${T.teal}35`, borderRadius: 8, padding: '4px 10px',
                  }}>
                    {r.substituteName || 'Proposed substitute'}
                  </span>
                </div>

                {r.reason && (
                  <div style={{ fontSize: 11.5, color: T.text, marginTop: 10, lineHeight: 1.5 }}>
                    <span style={{ color: T.textSec }}>Pharmacist: </span>{r.reason}
                  </div>
                )}

                <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 8, lineHeight: 1.5 }}>
                  Your order does not move until you answer. If you are unsure, decline and ask your doctor.
                </div>

                <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                  <button
                    onClick={() => decide(r, true)}
                    disabled={busyId === r.id}
                    style={{
                      flex: 1, padding: '10px 0', borderRadius: 10, border: 'none',
                      cursor: busyId === r.id ? 'default' : 'pointer',
                      fontSize: 12, fontWeight: 700, fontFamily: T.font,
                      color: T.white, background: T.teal, opacity: busyId === r.id ? 0.6 : 1,
                    }}
                  >
                    {busyId === r.id ? 'Sending…' : 'Accept substitute'}
                  </button>
                  <button
                    onClick={() => decide(r, false)}
                    disabled={busyId === r.id}
                    style={{
                      flex: 1, padding: '10px 0', borderRadius: 10,
                      border: `1px solid ${T.danger}40`, background: T.white,
                      cursor: busyId === r.id ? 'default' : 'pointer',
                      fontSize: 12, fontWeight: 700, fontFamily: T.font, color: T.danger,
                      opacity: busyId === r.id ? 0.6 : 1,
                    }}
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
